import React, { useRef, useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { inchesToPixels } from './sizeUtils';

// The reference box is a fixed pixel width shown on the calibration canvas.
const REF_BOX_PX = 200;    // px wide — user measures this with a ruler
const REF_REAL_W = 5;      // real-world reference width in inches (5" wide)
const REF_REAL_H = 3;      // real-world reference height in inches (3" tall)

const CalibrationStep = ({ onApply, onSkip, initialScreenWidthIn, initialViewDistIn }) => {
    // ── User inputs ────────────────────────────────────────────────────────────
    const [measuredIn, setMeasuredIn] = useState('');
    const [viewDistFt, setViewDistFt] = useState(
        Math.max(3, Math.round((initialViewDistIn || 72) / 12))
    );
    const [applied, setApplied] = useState(false);

    // Track canvas size so drawing re-fires after resize
    const [canvasPx, setCanvasPx] = useState(800);
    const calCanvasRef = useRef(null);

    // ── Derived calibration values ─────────────────────────────────────────────
    const measuredNum   = parseFloat(measuredIn);
    const validMeasure  = measuredNum > 0 && measuredNum < 30;
    const pixelsPerInch = validMeasure ? REF_BOX_PX / measuredNum : null;
    const derivedScreenWidthIn = pixelsPerInch ? canvasPx / pixelsPerInch : (initialScreenWidthIn || 24);
    const derivedViewDistIn    = viewDistFt * 12;

    // Reference zone apparent size in pixels on screen (using 5"×3" calibration reference)
    const aZonePx = useCallback((yards) => {
        if (!pixelsPerInch) return null;
        return {
            w: inchesToPixels(REF_REAL_W, canvasPx, derivedScreenWidthIn, derivedViewDistIn, yards),
            h: inchesToPixels(REF_REAL_H, canvasPx, derivedScreenWidthIn, derivedViewDistIn, yards),
        };
    }, [pixelsPerInch, canvasPx, derivedScreenWidthIn, derivedViewDistIn]);

    // Reference zone apparent SIZE in physical inches (for instruction text)
    const aZoneScreenIn = useCallback((yards) => ({
        w: (REF_REAL_W * derivedViewDistIn) / (yards * 36),
        h: (REF_REAL_H * derivedViewDistIn) / (yards * 36),
    }), [derivedViewDistIn]);

    // ── Resize canvas then draw ────────────────────────────────────────────────
    useEffect(() => {
        const canvas = calCanvasRef.current;
        if (!canvas) return;

        // Size the canvas to its CSS container
        const resize = () => {
            const parent = canvas.parentElement;
            if (!parent) return;
            canvas.width  = parent.clientWidth;
            canvas.height = parent.clientHeight;
            setCanvasPx(canvas.width);
        };
        resize();
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, []);

    // ── Drawing (re-runs whenever inputs or canvas size changes) ───────────────
    useEffect(() => {
        const canvas = calCanvasRef.current;
        if (!canvas || canvas.width === 0 || canvas.height === 0) return;
        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;

        ctx.clearRect(0, 0, w, h);
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, w, h);

        const cx = w / 2;
        const refBoxH = REF_BOX_PX * (REF_REAL_H / REF_REAL_W); // 5:3 → 200 × 120 px
        const refY = Math.max(20, h * 0.08);

        // ── Title ─────────────────────────────────────────────────────────────
        ctx.fillStyle = 'rgba(255,255,255,0.7)';
        ctx.font = 'bold 15px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('SCREEN CALIBRATION', cx, refY - 12);

        // ── Reference box (the thing to measure) ──────────────────────────────
        // Glow
        ctx.shadowColor = '#5dade2';
        ctx.shadowBlur = 16;
        ctx.strokeStyle = '#5dade2';
        ctx.lineWidth = 2;
        ctx.strokeRect(cx - REF_BOX_PX / 2, refY, REF_BOX_PX, refBoxH);
        ctx.shadowBlur = 0;

        // Fill
        ctx.fillStyle = 'rgba(93,173,226,0.06)';
        ctx.fillRect(cx - REF_BOX_PX / 2, refY, REF_BOX_PX, refBoxH);

        // Inner label
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = 'bold 12px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('MEASURE ME', cx, refY + refBoxH / 2 - 8);
        ctx.font = '11px Inter, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fillText(`${REF_REAL_W}" wide × ${REF_REAL_H}" tall at 7 yd`, cx, refY + refBoxH / 2 + 8);

        // Width arrow
        const arrowY = refY + refBoxH + 18;
        ctx.strokeStyle = '#5dade2';
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(cx - REF_BOX_PX / 2, arrowY);
        ctx.lineTo(cx + REF_BOX_PX / 2, arrowY);
        ctx.stroke();

        [[cx - REF_BOX_PX / 2, -1], [cx + REF_BOX_PX / 2, 1]].forEach(([ax, dir]) => {
            ctx.beginPath();
            ctx.moveTo(ax, arrowY);
            ctx.lineTo(ax + dir * 7, arrowY - 4);
            ctx.lineTo(ax + dir * 7, arrowY + 4);
            ctx.closePath();
            ctx.fillStyle = '#5dade2';
            ctx.fill();
        });
        ctx.fillStyle = '#5dade2';
        ctx.font = 'bold 12px Inter, monospace';
        ctx.textAlign = 'center';
        ctx.fillText(`${REF_BOX_PX} px`, cx, arrowY + 16);

        // ── A-zone preview boxes ───────────────────────────────────────────────
        const previewY = arrowY + 36;
        const az7 = aZonePx(7);
        const az3 = aZonePx(3);

        if (az7 && az3) {
            const e7 = aZoneScreenIn(7);
            const e3 = aZoneScreenIn(3);
            const leftX  = cx - w * 0.25;
            const rightX = cx + w * 0.25;

            // 7 yd
            ctx.fillStyle = '#2ecc71';
            ctx.font = 'bold 12px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('A-zone at 7 yards', leftX, previewY - 8);
            ctx.strokeStyle = '#2ecc71';
            ctx.lineWidth = 1.5;
            ctx.strokeRect(leftX - az7.w / 2, previewY, az7.w, az7.h);
            ctx.fillStyle = 'rgba(46,204,113,0.07)';
            ctx.fillRect(leftX - az7.w / 2, previewY, az7.w, az7.h);
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.font = '11px Inter, monospace';
            ctx.fillText(`~${e7.w.toFixed(1)}" × ${e7.h.toFixed(1)}"`, leftX, previewY + az7.h + 15);

            // 3 yd
            ctx.fillStyle = '#e67e22';
            ctx.font = 'bold 12px Inter, sans-serif';
            ctx.fillText('A-zone at 3 yards', rightX, previewY - 8);
            ctx.strokeStyle = '#e67e22';
            ctx.lineWidth = 1.5;
            ctx.strokeRect(rightX - az3.w / 2, previewY, az3.w, az3.h);
            ctx.fillStyle = 'rgba(230,126,34,0.07)';
            ctx.fillRect(rightX - az3.w / 2, previewY, az3.w, az3.h);
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.font = '11px Inter, monospace';
            ctx.fillText(`~${e3.w.toFixed(1)}" × ${e3.h.toFixed(1)}"`, rightX, previewY + az3.h + 15);

            // Verify hint
            ctx.fillStyle = 'rgba(255,255,255,0.2)';
            ctx.font = '11px Inter, sans-serif';
            const botY = previewY + Math.max(az7.h, az3.h) + 32;
            ctx.fillText('Hold a ruler to the screen to verify these match', cx, botY);
        } else {
            // Prompt
            ctx.fillStyle = 'rgba(255,255,255,0.18)';
            ctx.font = '13px Inter, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('↓ Measure the blue box with a ruler, then enter the width below', cx, previewY + 30);
        }
    }, [measuredIn, viewDistFt, canvasPx, aZonePx, aZoneScreenIn]);

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleApply = () => {
        if (!validMeasure) return;
        setApplied(true);
        onApply({ screenWidthIn: derivedScreenWidthIn, viewDistIn: derivedViewDistIn });
    };

    // ── Styles ────────────────────────────────────────────────────────────────
    const expect7 = aZoneScreenIn(7);
    const expect3 = aZoneScreenIn(3);

    const overlay = {
        position: 'absolute', inset: 0, zIndex: 20,
        pointerEvents: 'none',
    };
    const panel = {
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'rgba(10,10,22,0.96)',
        backdropFilter: 'blur(8px)',
        borderTop: '1px solid #1e3a5f',
        padding: '14px 20px 16px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        alignItems: 'flex-end',
        pointerEvents: 'all',
    };
    const fieldStyle = { display: 'flex', flexDirection: 'column', gap: '5px', minWidth: '155px' };
    const labelStyle = { color: '#777', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '1px' };
    const numInput = {
        background: '#0d0d1a',
        border: `1px solid ${validMeasure ? '#5dade2' : (measuredIn ? '#e74c3c' : '#2a2a3a')}`,
        borderRadius: '6px',
        color: '#fff',
        padding: '8px 10px',
        fontSize: '16px',
        fontFamily: 'monospace',
        width: '100px',
    };
    const applyBtn = {
        background: validMeasure ? '#0d2a44' : '#111',
        color: validMeasure ? '#5dade2' : '#444',
        border: `1px solid ${validMeasure ? '#5dade2' : '#222'}`,
        borderRadius: '6px',
        padding: '10px 22px',
        fontSize: '13px',
        fontWeight: 'bold',
        cursor: validMeasure ? 'pointer' : 'not-allowed',
        letterSpacing: '1px',
        whiteSpace: 'nowrap',
    };
    const skipBtn = {
        background: 'transparent',
        color: '#444',
        border: '1px solid #222',
        borderRadius: '6px',
        padding: '10px 14px',
        fontSize: '12px',
        cursor: 'pointer',
    };

    return (
        <div style={overlay}>
            <canvas
                ref={calCanvasRef}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
            />

            <div style={panel}>
                {/* Step 1 */}
                <div style={fieldStyle}>
                    <span style={labelStyle}>① Measure the blue box (ruler)</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                            id="cal-measured-in"
                            type="number"
                            step="0.1"
                            min="0.1"
                            max="20"
                            placeholder="e.g. 2.5"
                            value={measuredIn}
                            onChange={e => setMeasuredIn(e.target.value)}
                            style={numInput}
                        />
                        <span style={{ color: '#666', fontSize: '13px' }}>inches wide</span>
                    </div>
                    {validMeasure && (
                        <span style={{ color: '#5dade2', fontSize: '11px', fontFamily: 'monospace' }}>
                            → {(REF_BOX_PX / measuredNum).toFixed(1)} px&thinsp;/&thinsp;in
                        </span>
                    )}
                </div>

                {/* Step 2 */}
                <div style={fieldStyle}>
                    <span style={labelStyle}>② Distance from screen</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <input
                            id="cal-view-dist-ft"
                            type="range"
                            min="3" max="15" step="0.5"
                            value={viewDistFt}
                            onChange={e => setViewDistFt(Number(e.target.value))}
                            style={{ width: '120px' }}
                        />
                        <span style={{ color: '#fff', fontFamily: 'monospace', fontSize: '15px', minWidth: '44px' }}>
                            {viewDistFt} ft
                        </span>
                    </div>
                    <span style={{ color: '#444', fontSize: '11px' }}>Projected screen: 3–10 ft typical</span>
                </div>

                {/* Size hints (visible after measurement entered) */}
                {validMeasure && (
                    <div style={{ ...fieldStyle, minWidth: '190px' }}>
                        <span style={labelStyle}>A-zone on screen</span>
                        <div style={{ color: '#2ecc71', fontSize: '12px', fontFamily: 'monospace' }}>
                            7 yd: ~{expect7.w.toFixed(1)}&quot; × {expect7.h.toFixed(1)}&quot;
                        </div>
                        <div style={{ color: '#e67e22', fontSize: '12px', fontFamily: 'monospace' }}>
                            3 yd: ~{expect3.w.toFixed(1)}&quot; × {expect3.h.toFixed(1)}&quot;
                        </div>
                        <span style={{ color: '#444', fontSize: '10px' }}>measured on screen at these distances</span>
                    </div>
                )}

                {/* Actions */}
                <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto', alignSelf: 'center', pointerEvents: 'all' }}>
                    <button onClick={onSkip} style={skipBtn}>Skip</button>
                    <button onClick={handleApply} disabled={!validMeasure} style={applyBtn}>
                        {applied ? 'APPLIED ✓' : 'APPLY CALIBRATION'}
                    </button>
                </div>
            </div>
        </div>
    );
};

CalibrationStep.propTypes = {
    onApply: PropTypes.func.isRequired,
    onSkip: PropTypes.func.isRequired,
    initialScreenWidthIn: PropTypes.number,
    initialViewDistIn: PropTypes.number,
};

CalibrationStep.defaultProps = {
    initialScreenWidthIn: 24,
    initialViewDistIn: 72,
};

export default CalibrationStep;
